import React, { useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, CheckCircle2, Loader2 } from 'lucide-react';
import { Dialog, DialogContent, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import '@/components/public/uk/uk.css';

// Standalone lead / consultation modal for the Founder Guides page (spans all 5 markets).
export const GuidesEnquiryModal = ({ open, onOpenChange }) => {
  const [state, setState] = useState('idle');
  const [error, setError] = useState('');
  const [reference, setReference] = useState('');
  const requestId = useRef(null);
  const busy = state === 'sending';

  const submit = async (event) => {
    event.preventDefault();
    if (busy) return;
    const form = new FormData(event.currentTarget);
    if (!form.get('name').trim() || !form.get('message').trim()) {
      setError('Please add your name and a short description of your plans.');
      return;
    }
    setState('sending'); setError('');
    if (!requestId.current) requestId.current = crypto.randomUUID();
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/public/country-enquiries`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...Object.fromEntries(form), consent: form.get('consent') === 'on', source: 'founder-guides', request_id: requestId.current }),
      });
      if (!response.ok) throw new Error(response.status === 429 ? 'Too many enquiries. Please try again later.' : 'Your enquiry could not be submitted. Please try again.');
      const result = await response.json();
      setReference(result.id); setState('sent');
    } catch (err) { setError(err.message); setState('idle'); }
  };

  const handleOpenChange = (value) => {
    if (busy) return;
    if (!value) { setState('idle'); setError(''); requestId.current = null; }
    onOpenChange(value);
  };

  return <Dialog open={open} onOpenChange={handleOpenChange}>
    <DialogContent className="uk-enquiry-modal" data-testid="guides-enquiry-modal" closeTestId="guides-enquiry-close">
      <div className="uk-eyebrow">StartEase<span className="uk-sep" />Consultation</div>
      <DialogTitle className="uk-modal-title" data-testid="guides-enquiry-title">{state === 'sent' ? 'Your next chapter starts here.' : 'Let’s find the right solution.'}</DialogTitle>
      <DialogDescription data-testid="guides-enquiry-description">{state === 'sent' ? 'Your enquiry has been saved for the StartEase team.' : 'Tell us what you’re trying to do. We’ll review the scope with you before any commitment — no fixed packages, no pressure.'}</DialogDescription>
      {state === 'sent' ? <div className="uk-enquiry-success" role="status" data-testid="guides-enquiry-success">
        <CheckCircle2 size={36} /><p>Enquiry received</p><span data-testid="guides-enquiry-reference">Reference: {reference}</span>
        <button className="uk-button" data-testid="guides-enquiry-done" onClick={() => handleOpenChange(false)}>Done<ArrowRight size={16} /></button>
      </div> : <form onSubmit={submit} className="uk-enquiry-form" data-testid="guides-enquiry-form">
        <fieldset disabled={busy}>
          <div className="uk-form-grid">
            <div><Label htmlFor="fg-name">Full name</Label><Input id="fg-name" name="name" autoComplete="name" required maxLength={100} data-testid="guides-enquiry-name" /></div>
            <div><Label htmlFor="fg-email">Email address</Label><Input id="fg-email" name="email" type="email" autoComplete="email" required maxLength={254} data-testid="guides-enquiry-email" /></div>
          </div>
          <div><Label htmlFor="fg-company">Company name <span>(optional)</span></Label><Input id="fg-company" name="company" autoComplete="organization" maxLength={150} data-testid="guides-enquiry-company" /></div>
          <input type="hidden" name="country" value="United States" />
          <input type="hidden" name="service" value="Not sure yet" />
          <div><Label htmlFor="fg-message">Your plans</Label><Textarea id="fg-message" name="message" required maxLength={3000} rows={3} placeholder="Your activity, where you’re based, and what you need help with." data-testid="guides-enquiry-message" /></div>
          <div className="hidden" aria-hidden="true"><input name="website" tabIndex={-1} autoComplete="off" data-testid="guides-enquiry-website" /></div>
          <label className="uk-consent" htmlFor="fg-consent"><input type="checkbox" id="fg-consent" name="consent" required data-testid="guides-enquiry-consent" /><span>I agree to be contacted about this enquiry and have read the <Link to="/privacy" target="_blank" rel="noopener noreferrer" data-testid="guides-enquiry-privacy">privacy policy</Link>.</span></label>
          <p className="uk-fineprint" data-testid="guides-enquiry-privacy-note">Please don’t include identity documents, passwords or personal codes.</p>
          {error && <p role="alert" className="uk-form-error" data-testid="guides-enquiry-error">{error}</p>}
          <button className="uk-button uk-submit" type="submit" disabled={busy} data-testid="guides-enquiry-submit">{busy ? <>Submitting<Loader2 size={16} className="animate-spin" /></> : <>Send enquiry<ArrowRight size={16} /></>}</button>
        </fieldset>
      </form>}
    </DialogContent>
  </Dialog>;
};

export default GuidesEnquiryModal;
